import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
// import Table from '../../components/Table';
import { useTable, useSortBy, usePagination } from 'react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SyncSelect from 'react-select';
import DropdownIndicator from '../../components/Filters/Filter/DropdownIndicator';
import '../../components/Filters/Filter/styles/DropdownIndicator.css';

function ReportTable({
  columnHeaders,
  data,
  defaultSorts,
  fields,
  pivots,
  error,
  loading
}) {
  const rowData = useMemo(() => data, [data]);
  const columns = useMemo(() => columnHeaders, [columnHeaders]);

  const initialState = {
    pageIndex: 0,
    pageSize: 25
  };
  if (defaultSorts.length) {
    initialState.sortBy = defaultSorts.map(id => ({ id, desc: true }));
  }

  const defaultPageOptions = [10, 25, 50, 100];

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    // eslint-disable-next-line
    state: { sortBy, pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data: rowData,
      initialState
    },
    useSortBy,
    usePagination
  );

  const changePage = e => {
    e.stopPropagation();
    const val = e.currentTarget.value;
    if (typeof val === 'string' && val === 'previous') {
      previousPage();
    } else if (typeof val === 'string' && val === 'next') {
      nextPage();
    } else {
      gotoPage(Number(val));
    }
  };

  const renderSortIcon = column => {
    if (!column.isSorted) {
      return null;
    }

    return column.isSortedDesc ? (
      <FontAwesomeIcon
        className="tvt-report-builder-table__sort-icon"
        icon="caret-down"
        size="1x"
      />
    ) : (
      <FontAwesomeIcon
        className="tvt-report-builder-table__sort-icon"
        icon="caret-up"
        size="1x"
      />
    );
  };

  const renderLoadingTable = () => (
    <div className="tvt-report-builder-table loading-state">
      <div className="tvt-report-builder-table__header loading-state">
        {pivots.map(pivot => (
          <div
            key={pivot}
            className="tvt-report-builder-table__loading-pivot-row"
          >
            <div className="th">{pivot.label}</div>
          </div>
        ))}
        <div className="tr">
          {fields.map(field => (
            <div key={field.label} className="th">
              {field.label}
            </div>
          ))}
        </div>
      </div>
      <div>
        {[...Array(25)].map(i => (
          <div key={i} className="tr tvt-report-builder-table__loading-row">
            {[...Array(fields.length)].map(() => (
              <div className="tvt-report-builder-table__loading-bar" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  const renderTable = () => (
    <div {...getTableProps()} className="tvt-report-builder-table">
      <div className="tvt-report-builder-table__header">
        {headerGroups.map(headerGroup => (
          <div {...headerGroup.getHeaderGroupProps()} className="tr">
            {headerGroup.headers.map(column => (
              <div
                {...column.getHeaderProps(
                  column.getSortByToggleProps({
                    title: column.hoverTitle || 'Sort'
                  })
                )}
                className={`th ${column.className ||
                  'tvt-report-builder-table__item'}`}
              >
                {column.render('Header')}
                {renderSortIcon(column)}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div {...getTableBodyProps()}>
        {page.map(row => {
          prepareRow(row);
          return (
            <div className="tr" {...row.getRowProps()}>
              {row.cells.map(cell => (
                <div
                  {...cell.getCellProps()}
                  className={`td ${cell.column.className ||
                    'tvt-report-builder-table__item'}`}
                >
                  {cell.render('Cell')}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="tvt-report-builder-table__error-container">
        <FontAwesomeIcon
          className="tvt-report-builder-table__error-exclamation"
          icon="exclamation-triangle"
          size="1x"
        />{' '}
        <span>
          Uh oh! There was an error fetching your request. Please try again or
          contact your sales representative.
        </span>
      </div>
    );
  }

  return (
    <div className="tvt-report-builder-table__container">
      {!loading ? renderTable() : renderLoadingTable()}
      <div className="tvt-report-builder__pagination">
        <SyncSelect
          classNamePrefix="tvt-report-builder__pageSize-select"
          className="tvt-report-builder__pageSize-select"
          components={{ DropdownIndicator, IndicatorSeparator: null }}
          placeholder={`${pageSize} rows`}
          options={defaultPageOptions.map(pageSize => ({
            label: `${pageSize} rows`,
            value: pageSize
          }))}
          value={pageSize}
          onChange={opt => {
            setPageSize(Number(opt.value));
          }}
        />
        <button
          className="tvt-report-builder__page-buttons"
          onClick={changePage}
          value={0}
          disabled={!canPreviousPage}
        >
          <FontAwesomeIcon
            className="tvt-report-builder__page-buttons-caret"
            icon="angle-double-left"
            size="1x"
          />
        </button>
        <button
          className="tvt-report-builder__page-buttons"
          onClick={changePage}
          value="previous"
          disabled={!canPreviousPage}
        >
          <FontAwesomeIcon
            className="tvt-report-builder__page-buttons-caret"
            icon="angle-left"
            size="1x"
          />
        </button>
        <span className="tvt-report-builder__page-selector">
          <input
            className="tvt-report-builder__pageSize-select-input"
            type="number"
            value={pageIndex + 1}
            onChange={e => {
              const p = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(p);
            }}
          />
          of {pageOptions.length}
        </span>
        <button
          className="tvt-report-builder__page-buttons"
          onClick={changePage}
          value="next"
          disabled={!canNextPage}
        >
          <FontAwesomeIcon
            className="tvt-report-builder__page-buttons-caret"
            icon="angle-right"
            size="1x"
          />
        </button>
        <button
          className="tvt-report-builder__page-buttons"
          onClick={changePage}
          value={pageCount - 1}
          disabled={!canNextPage}
        >
          <FontAwesomeIcon
            className="tvt-report-builder__page-buttons-caret"
            icon="angle-double-right"
            size="1x"
          />
        </button>{' '}
      </div>
    </div>
  );
}

ReportTable.propTypes = {
  columnHeaders: PropTypes.arrayOf(PropTypes.shape({})),
  data: PropTypes.arrayOf(PropTypes.shape({})),
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  pivots: PropTypes.arrayOf(PropTypes.shape({})),
  defaultSorts: PropTypes.arrayOf(PropTypes.string),
  error: PropTypes.bool,
  loading: PropTypes.bool
};

ReportTable.defaultProps = {
  columnHeaders: [],
  data: [],
  fields: [],
  pivots: [],
  defaultSorts: [],
  error: false,
  loading: false
};

export default ReportTable;
